import { useCallback, useEffect, useMemo } from "react";

import { InfinityLoader } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { fetchClusterDigDeeperItems } from "domains/clusters/actions";
import { createDigDeeperSelector } from "domains/clusters/selectors";
import { KnowledgeListItem } from "domains/publication/components/KnowledgeListItem";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { getCanAccessMonitor } from "entities/permissions/selectors";

type DigDeeperListProps = { clusterId: string };

export const DigDeeperList = ({ clusterId }: DigDeeperListProps) => {
  const dispatch = useDispatch();
  const digDeeper = useSelector(createDigDeeperSelector(clusterId));
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);

  const knowledgesSelector = useMemo(() => createKnowledgesSelector(digDeeper?.ids ?? []), [digDeeper?.ids]);

  const items = useSelector(knowledgesSelector);

  const hasNextPage = digDeeper?.hasNextPage ?? false;
  const isPending = digDeeper?.isPending ?? false;
  const offset = digDeeper?.offset ?? 0;

  useEffect(() => {
    dispatch(fetchClusterDigDeeperItems({ id: clusterId, offset: 0 }));
  }, [dispatch, clusterId]);

  const loadNextPage = useCallback(
    () => dispatch(fetchClusterDigDeeperItems({ id: clusterId, offset: offset + 1 })),
    [dispatch, clusterId, offset],
  );

  const getToLink = (knowledgeId: string) => `/monitor/emerging-themes/${clusterId}/dig-deeper/${knowledgeId}`;

  if (items.length === 0) {
    return null;
  }

  return (
    <InfinityLoader isPending={isPending} hasNextPage={hasNextPage} loadNextPage={loadNextPage}>
      {items.map((item, index) => (
        <KnowledgeListItem
          key={item.id}
          link={getToLink(item.id)}
          shareLink={getToLink(item.id)}
          knowledge={item}
          isLocked={!hasAccessToMonitor && index !== 0}
          isFirstLocked={index === 1}
          trackPage="Cluster detail"
        />
      ))}
    </InfinityLoader>
  );
};
